import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import { Lock, Mail, Shield } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [googleProcessing, setGoogleProcessing] = useState(
    typeof window !== 'undefined' && window.location.hash.includes('session_id=')
  );
  const navigate = useNavigate();
  const processedRef = useRef(false);

  // Exchange an Emergent Google session (returned in the URL fragment) for the app JWT
  useEffect(() => {
    if (!window.location.hash.includes('session_id=')) return;
    if (processedRef.current) return;
    processedRef.current = true;
    const sessionId = new URLSearchParams(window.location.hash.slice(1)).get('session_id');
    window.history.replaceState({}, document.title, '/admin/login');
    (async () => {
      try {
        const { data } = await axios.post(`${API}/admin/auth/google/session`, { session_id: sessionId });
        if (data.access_token) {
          sessionStorage.setItem('adminToken', data.access_token);
          toast({ title: 'Signed in with Google', description: 'Welcome back to the admin dashboard!' });
          navigate('/admin/dashboard');
          return;
        }
        throw new Error('No token returned');
      } catch (error) {
        toast({
          title: 'Google sign-in failed',
          description: error.response?.data?.detail || 'This Google account may not be authorized.',
          variant: 'destructive',
        });
        setGoogleProcessing(false);
      }
    })();
  }, [navigate]);

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/admin/login';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    try {
      await axios.post(`${API}/admin/auth/forgot-password`, { email: forgotEmail });
      setForgotSent(true);
    } catch (error) {
      toast({
        title: 'Something went wrong',
        description: error.response?.data?.detail || 'Please try again shortly.',
        variant: 'destructive',
      });
    } finally {
      setForgotLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/admin/auth/login`, {
        email,
        password
      });

      if (response.data.access_token) {
        // Store token in sessionStorage (more secure than localStorage)
        sessionStorage.setItem('adminToken', response.data.access_token);
        
        toast({
          title: 'Login Successful',
          description: 'Welcome back to the admin dashboard!',
        });
        
        // Redirect to admin dashboard
        navigate('/admin/dashboard');
      }
    } catch (error) {
      toast({
        title: 'Login Failed',
        description: error.response?.data?.detail || 'Invalid email or password',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <div className="bg-[#FDB913] p-4 rounded-full">
              <Shield className="w-12 h-12 text-gray-900" />
            </div>
          </div>
          <CardTitle className="text-2xl font-bold text-gray-900">
            Admin Login
          </CardTitle>
          <p className="text-gray-600 mt-2">The Lokal Winery Dashboard</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="flex items-center gap-2">
                <Mail className="w-4 h-4" />
                Email Address
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@thelokal.ca"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="flex items-center gap-2">
                <Lock className="w-4 h-4" />
                Password
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
              />
            </div>

            <Button
              type="submit"
              className="w-full bg-[#FDB913] hover:bg-[#E5A50A] text-gray-900 font-semibold"
              disabled={isLoading}
            >
              {isLoading ? 'Logging in...' : 'Login to Dashboard'}
            </Button>
          </form>

          <div className="flex items-center gap-3 my-5">
            <div className="h-px bg-gray-200 flex-1" />
            <span className="text-xs text-gray-400 uppercase tracking-wide">or</span>
            <div className="h-px bg-gray-200 flex-1" />
          </div>

          <Button
            type="button"
            variant="outline"
            onClick={handleGoogleLogin}
            disabled={googleProcessing}
            className="w-full border-gray-300 font-medium flex items-center justify-center gap-2"
            data-testid="google-signin-button"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" aria-hidden="true">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.76h3.56c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.56-2.76c-.98.66-2.23 1.06-3.72 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.11a6.6 6.6 0 0 1 0-4.22V7.05H2.18a11 11 0 0 0 0 9.9l3.66-2.84z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.05l3.66 2.84C6.71 7.3 9.14 5.38 12 5.38z"/>
            </svg>
            {googleProcessing ? 'Completing Google sign-in…' : 'Sign in with Google'}
          </Button>

          <div className="mt-4 text-center">
            <button
              type="button"
              onClick={() => { setShowForgot(true); setForgotSent(false); setForgotEmail(email); }}
              className="text-sm text-[#722F37] hover:underline font-medium"
              data-testid="forgot-password-link"
            >
              Forgot password?
            </button>
          </div>

          <div className="mt-6 text-center text-sm text-gray-600">
            <p>Secure admin access only</p>
          </div>
        </CardContent>
      </Card>

      <Dialog open={showForgot} onOpenChange={(o) => { setShowForgot(o); if (!o) setForgotSent(false); }}>
        <DialogContent className="max-w-md" data-testid="forgot-password-dialog">
          <DialogHeader>
            <DialogTitle>Reset your password</DialogTitle>
            <DialogDescription>
              Enter your admin email and we'll send you a link to set a new password.
            </DialogDescription>
          </DialogHeader>
          {forgotSent ? (
            <div className="space-y-4 py-2" data-testid="forgot-sent">
              <p className="text-sm text-gray-700">
                If an admin account exists for that email, a reset link is on its way. The link expires in 30 minutes.
              </p>
              <p className="text-xs text-gray-500">
                Didn't get it? Check spam, or try again in a few minutes.
              </p>
              <Button className="w-full" onClick={() => setShowForgot(false)} data-testid="forgot-close">Close</Button>
            </div>
          ) : (
            <form onSubmit={handleForgot} className="space-y-4 py-2">
              <div className="space-y-2">
                <Label htmlFor="forgot-email" className="flex items-center gap-2"><Mail className="w-4 h-4" /> Admin email</Label>
                <Input
                  id="forgot-email"
                  type="email"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  placeholder="info@thelokal.ca"
                  required
                  data-testid="forgot-email-input"
                />
              </div>
              <Button
                type="submit"
                className="w-full bg-[#722F37] hover:bg-[#5c252c] text-white font-semibold"
                disabled={forgotLoading}
                data-testid="forgot-submit"
              >
                {forgotLoading ? 'Sending…' : 'Send reset link'}
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminLogin;
